import { Theme, useTheme } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useGetIdentity, useGetOne, UserIdentity } from "react-admin";
import { createPartnerTheme, createPartnerDarkTheme, PartnerBranding } from "@/styles/theme/theme";
import { DisplayMode, Partner, ResourceName } from "@/types";
import { AUTH_CONSTANTS } from "@/constants";
import { getEnvironment, ENV_CONFIG } from "@/utils/environment";
import { pickBranding, writeBrandingCache } from "@/utils/brandingSync";

/** Extended branding cache that includes logo + name alongside color fields. */
interface CachedBranding extends PartnerBranding {
  logo_image?: string;
  logo_image_dark?: string;
  partner_company_name?: string;
}

/**
 * Reads cached partner branding from sessionStorage (written during login).
 * Used as an instant fallback before the partner fetch completes,
 * preventing a flash of default-themed UI on page reload.
 */
const getCachedBranding = (): CachedBranding | null => {
  try {
    const cached = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.PARTNER_BRANDING);
    if (!cached) return null;
    const branding = JSON.parse(cached);
    return branding.brand_primary ? branding : null;
  } catch {
    return null;
  }
};

/**
 * Extracts branding fields from a fetched partner record.
 *
 * Reads the canonical dedicated `branding` column first, falling back to the legacy
 * `configuration.branding` for partners not yet migrated/cut over (#4116, retired by #4758).
 */
const extractBranding = (partner: Partner): PartnerBranding | null => {
  const branding = pickBranding(partner);
  if (!branding?.brand_primary) return null;
  return {
    brand_primary: branding.brand_primary,
    brand_secondary: branding.brand_secondary || branding.brand_primary,
    brand_primary_dark: branding.brand_primary_dark || branding.brand_primary,
    brand_secondary_dark: branding.brand_secondary_dark || branding.brand_secondary || branding.brand_primary,
  };
};

/**
 * App-wide theme hook that layers partner branding over the default Aegle theme.
 *
 * Priority: fetched partner data > sessionStorage cache > default theme.
 * The cache is only used while the partner fetch is in flight, preventing
 * a flash of un-branded UI on page reload. Once the fetch completes,
 * the live data always wins (so admin color changes propagate without logout).
 */
export const useAppTheme = (): {
  theme: Theme;
  isDarkMode: boolean;
  hasPartnerBranding: boolean;
  logoImage: string | null;
  logoImageDark: string | null;
} => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === DisplayMode.DARK;

  const { data: identity } = useGetIdentity();
  const homePartnerId = (identity as UserIdentity)?.partnerId;

  // Use the active partner (set by the switcher) if available, otherwise fall back
  // to the home partner from the user's identity. Without this, switching to a
  // different partner would still fetch the home partner's branding and overwrite
  // the sessionStorage cache, causing a flash of wrong colors.
  const activePartnerId = sessionStorage.getItem(AUTH_CONSTANTS.STORAGE_KEYS.ACTIVE_PARTNER_ID);
  const partnerId = activePartnerId || homePartnerId;

  const { data: partner } = useGetOne(ResourceName.PARTNERS, { id: partnerId }, { enabled: !!partnerId });

  // Keep sessionStorage cache in sync with fetched data
  // so page reloads always have the latest branding.
  // Only write when the fetched partner IS the active partner — react-admin's
  // cache can update with a different partner's data after an inline edit.
  useEffect(() => {
    if (partner?.id && activePartnerId && String(partner.id) !== activePartnerId) return;
    writeBrandingCache({
      branding: partner ? extractBranding(partner) : null,
      logo_image: partner?.logo_image,
      logo_image_dark: partner?.logo_image_dark,
      partner_company_name: partner?.partner_company_name,
    });
  }, [partner]);

  const partnerTheme = useMemo(() => {
    const branding = partner ? extractBranding(partner) : getCachedBranding();

    if (!branding) return null;

    return isDarkMode ? createPartnerDarkTheme(branding) : createPartnerTheme(branding);
  }, [partner, isDarkMode]);

  // Resolve logo: live partner data (top-level columns) > cached branding
  // Use || instead of ?? so empty strings fall through to the cached value
  const logoImage = getCachedBranding()?.logo_image || null;
  const logoImageDark = getCachedBranding()?.logo_image_dark || null;

  // Update the browser favicon to match the partner's logo
  useEffect(() => {
    const faviconLogo = isDarkMode ? logoImageDark || logoImage : logoImage;
    if (!faviconLogo) return;

    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = faviconLogo;
  }, [logoImage, logoImageDark, isDarkMode]);

  // Update the browser tab title with environment + partner name
  useEffect(() => {
    const env = getEnvironment();
    const envPrefix = env !== "production" ? `[${ENV_CONFIG[env].label}] ` : "";
    const partnerName = partner?.partner_company_name || getCachedBranding()?.partner_company_name;
    const partnerSuffix = partnerName ? `${partnerName} — ` : "";
    document.title = `${envPrefix}${partnerSuffix}Claims Platform`;
  }, [partner]);

  return {
    theme: partnerTheme ?? theme,
    isDarkMode,
    hasPartnerBranding: !!partnerTheme,
    logoImage,
    logoImageDark,
  };
};
